import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import ReactDOM from 'react-dom';
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Redirect, Switch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { Spinner } from 'react-activity';
import blue from '@material-ui/core/colors/blue';
// Styles
import './utils/tailwind/css/style.css';
import './utils/tailwind/fontawesome/all.min.css';
import './utils/tailwind/css/videocall.css';
// Stores
import stores from './stores';
import store from './sessionStorage';
// Utils
import { PATH } from './utils/Constants';
import {
    BRANCH_ADMIN_BASEPATH,
    INS_SUPER_ADMIN_BASEPATH,
    LEVEL_ONE_ADMIN_BASEPATH,
    LEVEL_TWO_ADMIN_BASEPATH,
    WHITE_BOARD,
    NON_USER_SESSION,
} from './utils/routes/path';
import { isWhiteLabled } from './utils/URL';
import './utils/WhiteLabelThemeConfiguration';
import {
    screens as whiteLabeledScreens,
    screen as whiteLabeledScreen,
} from './whitelabelScreenConfiguration';
// Institution  Modal Components
// Admin Routes
import InsSuperAdminLayout from './voim/component/admin/super-admin';
import LevelOneAdminLayout from './voim/component/admin/level-one-admin';
import LevelTwoAdminLayout from './voim/component/admin/level-two-admin';
import BranchAdminLayout from './voim/component/admin/branch-admin';
// Student
import ImPatientDashBoard from './voim/component/student/Dashboard';
import ImStudentSession from './voim/component/student/session/SessionList';
import ImStudentCreateSession from './voim/component/student/session/CreateSession';
import ImPatientWaitingRoom from './voim/component/student/WaitingRoom';
// Educator
import EndUserChangePassword from './voim/component/common/EndUserChangePassword';
import ImPhysicianDashBoard from './voim/component/tutor/Dashboard';
import ImPhysicianManageSchedule from './voim/component/tutor/ManageSchedule';
import ImEducatorSession from './voim/component/tutor/session/SessionList';
import ImEducatorCreateSession from './voim/component/tutor/session/CreateSession';
import ImWaitingRoom from './voim/component/tutor/WaitingRoom';
import ImActiveVisit from './voim/component/tutor/video';
import IncomingCallVOIM from './voim/component/student/incomingCall';
import ImMyStudents from './voim/component/tutor/MyStudents';
import CustomFinancialReport from './voim/component/tutor/reports/CustomFinancialReport';
// Auth
import { isLoggedIn } from './utils/AthuService';
// Common Components
import Notification from './voim/component/common/Notification';
import NotFound from './voim/component/common/NotFound';
import NetworkScreen from './voim/component/common/NetworkScreen';
import ForgetPassword from './voim/ForgetPassword';
import ChangePassword from './voim/component/common/ChangePassword';
import StudentProfile from './voim/component/student/StudentProfile';
import EducatorProfile from './voim/component/tutor/EducatorProfile';
import ScreenShotEditModal from './voim/component/tutor/video/ScreenShotEditModal';
import StudentSummary from './voim/component/tutor/StudentSummary';
import { Student, Tutor } from './utils/common/NamingTerms';
import ReferralEducator from './voim/component/tutor/session/ReferralEducator';
import EducatorSubjectReferralSummary from './voim/component/tutor/EducatorSubjectReferralSummary';
import StudentSubjectReferralSummary from './voim/component/student/StudentSubjectReferralSummary';
import MessageScreen from './voim/component/tutor/message/MessageScreen';
import StudentMessageScreen from './voim/component/student/message/StudentMessageScreen';
import bypassing from './voim/bypassing';
import { initializeWhiteboardCleanup } from './utils/WhiteboardSessionStorage';

// Initialize whiteboard cleanup system on app startup
// This ensures expired data is cleaned up even after browser close, system restart, etc.
initializeWhiteboardCleanup();

const THEME = createTheme({
    typography: {
        fontFamily: ['DM Sans', 'sans-serif'].join(','),
    },
    _palette: {
        primary: blue,
    },
    get palette() {
        return this._palette;
    },
    set palette(value) {
        this._palette = value;
    },
});

const PrivateRoute = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) =>
            isLoggedIn() ? (
                store.get('userSession') ? (
                    <Component {...props} />
                ) : (
                    <Redirect to={PATH.INDEX} />
                )
            ) : (
                <Redirect to={PATH.INDEX} />
            )
        }
    />
);

const PrivateRoutePatient = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) =>
            isLoggedIn() ? (
                store.get('userSession').role === Student ? (
                    <Component {...props} />
                ) : (
                    <Redirect to={PATH.INDEX} />
                )
            ) : (
                <Redirect to={PATH.INDEX} />
            )
        }
    />
);

const PrivateRoutePhysician = ({ component: Component, ...rest }) => (
    <Route
        {...rest}
        render={(props) =>
            isLoggedIn() ? (
                store.get('userSession').role === Tutor ? (
                    <Component {...props} />
                ) : (
                    <Redirect to={PATH.INDEX} />
                )
            ) : (
                <Redirect to={PATH.INDEX} />
            )
        }
    />
);

const LandingCustomRouteRoute = ({ screen, ...props }) => {
    const screenObject = whiteLabeledScreens.find(
        (whiteLabeledScreenObject) =>
            whiteLabeledScreenObject.whiteLabeled === isWhiteLabled &&
            whiteLabeledScreenObject.screen === screen
    );
    return (
        <Route {...props} component={screenObject?.component ? screenObject.component : NotFound} />
    );
};

PrivateRoute.propTypes = {
    component: PropTypes.any,
};

PrivateRoutePatient.propTypes = {
    component: PropTypes.any,
};

PrivateRoutePhysician.propTypes = {
    component: PropTypes.any,
};

ReactDOM.render(
    <ThemeProvider theme={THEME}>
        <Provider store={stores}>
            <div>
                <Router>
                    <div>
                        <Suspense
                            fallback={
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginTop: '10px',
                                    }}
                                >
                                    <Spinner
                                        color="rgb(0,0,0)"
                                        style={{ weight: '100px', height: '100px' }}
                                    />
                                </div>
                            }
                        >
                            <Switch>
                                <Route exact path={WHITE_BOARD} component={ScreenShotEditModal} />
                                <Route exact path={NON_USER_SESSION} component={ImActiveVisit} />
                                <Route exact path={`${PATH.AUTH}`} component={bypassing} />
                                {/* Landing Components */}
                                <LandingCustomRouteRoute
                                    exact
                                    path={PATH.INDEX}
                                    screen={whiteLabeledScreen.login}
                                />
                                {/* Institution Modal Components */}
                                {/* Student */}
                                <PrivateRoute
                                    exact
                                    path={`${PATH.IM_PATIENT.CHANGE_PASSWORD}/:userId`}
                                    component={EndUserChangePassword}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.DASHBOARD}/:userId`}
                                    component={ImPatientDashBoard}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.MY_PROFILE}/:userId`}
                                    component={StudentProfile}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.APPOINTMENT}/:userId`}
                                    component={ImStudentSession}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.CREATE_APPOINTMENT}/:userId/create`}
                                    component={ImStudentCreateSession}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.CREATE_APPOINTMENT}/:userId/update`}
                                    component={ImStudentCreateSession}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.WAITINGROOM}/:userId/:visitId`}
                                    component={ImPatientWaitingRoom}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.VIDEO_CALL}/:visitId/:userType/:userId`}
                                    component={ImActiveVisit}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.VIDEO_CALL}/:visitId/:userType/:userId/classroom`}
                                    component={ImActiveVisit}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.VIDEO_CALL}/:visitId/:userType/:userId/broadcast`}
                                    component={ImActiveVisit}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.SUBJECT_REFERRAL_SUMMARY}/:receiverId/:id`}
                                    component={StudentSubjectReferralSummary}
                                />
                                <PrivateRoutePatient
                                    exact
                                    path={`${PATH.IM_PATIENT.MESSAGES}/:userId`}
                                    component={StudentMessageScreen}
                                />
                                {/* Educator */}
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.DASHBOARD}/:userId`}
                                    component={ImPhysicianDashBoard}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.MY_PROFILE}/:userId`}
                                    component={EducatorProfile}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.MANAGE_SCHEDULE}/:userId`}
                                    component={ImPhysicianManageSchedule}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.APPOINTMENT}/:userId`}
                                    component={ImEducatorSession}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.APPOINTMENT}/:userId/create`}
                                    component={ImEducatorCreateSession}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.APPOINTMENT}/:userId/update`}
                                    component={ImEducatorCreateSession}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.WAITINGROOM}/:userId/:visitId`}
                                    component={ImWaitingRoom}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.SUMMARY}/:userId/:studentId`}
                                    component={StudentSummary}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.CHANGE_PASSWORD}/:userId`}
                                    component={EndUserChangePassword}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.MY_STUDENTS}/:userId`}
                                    component={ImMyStudents}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.VIDEO_CALL}/:visitId/:userType/:userId`}
                                    component={ImActiveVisit}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.VIDEO_CALL}/:visitId/:userType/:userId/:patientUserId`}
                                    component={ImActiveVisit}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.VIDEO_CALL}/:visitId/:userType/:userId/classroom`}
                                    component={ImActiveVisit}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.VIDEO_CALL}/:visitId/:userType/:userId/broadcast`}
                                    component={ImActiveVisit}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.FINANCIAL_REPORT}/:userId`}
                                    component={CustomFinancialReport}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.REFERRAL}/:userId`}
                                    component={ReferralEducator}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.SUBJECT_REFERRAL_SUMMARY}/:receiverId/:id`}
                                    component={EducatorSubjectReferralSummary}
                                />
                                <PrivateRoutePhysician
                                    exact
                                    path={`${PATH.IM_PHYSICIAN.MESSAGES}/:userId`}
                                    component={MessageScreen}
                                />
                            </Switch>

                            <Route component={IncomingCallVOIM} />

                            {/* Admin Layout Components */}
                            <Route
                                path={INS_SUPER_ADMIN_BASEPATH}
                                component={InsSuperAdminLayout}
                            />
                            <Route path={BRANCH_ADMIN_BASEPATH} component={BranchAdminLayout} />
                            <Route
                                path={LEVEL_ONE_ADMIN_BASEPATH}
                                component={LevelOneAdminLayout}
                            />
                            <Route
                                path={LEVEL_TWO_ADMIN_BASEPATH}
                                component={LevelTwoAdminLayout}
                            />
                            {/* Common Component */}
                            <Route exact path={PATH.NOT_FOUND} component={NotFound} />
                            <Route exact path={PATH.FORGET_PASSWORD} component={ForgetPassword} />
                            <PrivateRoute
                                exact
                                path={PATH.CHANGE_PASSWORD}
                                component={ChangePassword}
                            />
                            {/* Notification  Message */}
                            <Notification />
                        </Suspense>
                    </div>
                    <NetworkScreen />
                </Router>
            </div>
        </Provider>
    </ThemeProvider>,
    document.getElementById('root')
);
